import { getItemDef } from "../sim/items";
import { shipExtent } from "../sim/ship";
import type { AsteroidProxy, Ship, SpaceItem } from "../sim/types";
import type { CameraState, Renderer } from "./renderer";

/** Zoom (CSS px per cell, i.e. getZoom()) thresholds for the global
 *  sprite→dot crossfade. At/above ZOOM_FULL every entity draws its full
 *  per-module silhouette; at/below ZOOM_DOT every entity is a dot; between, all
 *  sprites cross-fade to dots together.
 *
 *  The decision is purely zoom-based — an entity's own on-screen size never
 *  enters into it — so small debris stays fully drawn while you're zoomed in
 *  and only collapses once you've actually zoomed out, the same moment as
 *  everything else. */
const ZOOM_FULL = 4;
const ZOOM_DOT = 1;

/** A dot is sized to the entity's real on-screen footprint, clamped to this
 *  device-px range: never smaller than DOT_MIN_PX (so debris stays visible as a
 *  point) nor larger than DOT_MAX_PX (so a big ship reads as a bigger dot than a
 *  pebble without dominating the view). Tracking the real footprint also keeps
 *  the dot aligned with the silhouette through the crossfade. */
const DOT_MIN_PX = 4;
const DOT_MAX_PX = 48;

/** tiles.wgsl flag: reuse the emissivity slot as a 0..1 alpha scale. */
const FLAG_ALPHA_MULT = 4096;

/** Per-entity draw decision for one frame.
 *   - cull:     off-screen; push nothing.
 *   - full:     draw the full per-module silhouette.
 *   - dotAlpha: if > 0, also draw the LOD dot at this opacity (1 = pure dot,
 *               full=false; a fractional value = mid cross-fade, full=true). */
export interface LodResult {
  cull: boolean;
  full: boolean;
  dotAlpha: number;
}

// Shared singletons for the common cases so classifying 1000+ bodies per frame
// allocates nothing.
const CULL: LodResult = { cull: true, full: false, dotAlpha: 0 };
const FULL: LodResult = { cull: false, full: true, dotAlpha: 0 };
const DOT: LodResult = { cull: false, full: false, dotAlpha: 1 };

/** Per-frame view parameters for LOD/culling, derived once after the camera is
 *  synced to the canvas. */
export interface ViewLod {
  camX: number;
  camY: number;
  /** World-space radius of the circle that circumscribes the viewport — a
   *  rotation-independent cull bound. */
  screenR: number;
  /** Device pixels per world metre. */
  pxPerWorld: number;
  /** Global zoom-based sprite/dot decision, shared by every on-screen entity
   *  this frame. */
  lod: LodResult;
}

function classifyByZoom(zoom: number): LodResult {
  if (zoom >= ZOOM_FULL) return FULL;
  if (zoom <= ZOOM_DOT) return DOT;
  // In the band: silhouette stays fully drawn while the dot fades in on top.
  return { cull: false, full: true, dotAlpha: (ZOOM_FULL - zoom) / (ZOOM_FULL - ZOOM_DOT) };
}

export function makeViewLod(cam: CameraState, pixelW: number, zoom: number): ViewLod {
  const pxPerWorld = cam.worldW > 0 ? pixelW / cam.worldW : 1;
  const screenR = 0.5 * Math.hypot(cam.worldW, cam.worldH);
  return { camX: cam.x, camY: cam.y, screenR, pxPerWorld, lod: classifyByZoom(zoom) };
}

function offScreen(view: ViewLod, x: number, y: number, ext: number): boolean {
  const dx = x - view.camX;
  const dy = y - view.camY;
  const bound = view.screenR + ext;
  return dx * dx + dy * dy > bound * bound;
}

/** Every on-screen ship — the player, NPCs, asteroids, mined or pristine —
 *  follows the same global zoom fade and collapses to a dot on zoom-out. The
 *  worked-over state of a body is preserved in its module data regardless, so
 *  it's drawn exactly as left once you zoom back in. Physics is unaffected:
 *  everything within the 2km active zone keeps simulating fully no matter how
 *  it's drawn. */
export function classifyShip(view: ViewLod, ship: Ship): LodResult {
  const ext = shipExtent(ship);
  if (offScreen(view, ship.body.pos.x, ship.body.pos.y, ext)) return CULL;
  return view.lod;
}

export function classifyItem(view: ViewLod, it: SpaceItem): LodResult {
  const ext = getItemDef(it.defId).sizeM * 0.5;
  if (offScreen(view, it.pos.x, it.pos.y, ext)) return CULL;
  return view.lod;
}

/** World-space dot size for an entity of the given world extent (half-size):
 *  its real footprint, clamped to [DOT_MIN_PX, DOT_MAX_PX] on screen. */
function dotSize(view: ViewLod, ext: number): number {
  const px = Math.min(DOT_MAX_PX, Math.max(DOT_MIN_PX, 2 * ext * view.pxPerWorld));
  return px / view.pxPerWorld;
}

export function pushShipDot(r: Renderer, ship: Ship, view: ViewLod, alpha: number): void {
  const uv = r.atlas.lookup.get(ship.isAsteroid ? "lod_dot_rock" : "lod_dot_ship");
  if (!uv) return;
  const size = dotSize(view, shipExtent(ship));
  r.pushTileRaw(
    ship.body.pos.x, ship.body.pos.y,
    size, size,
    0, FLAG_ALPHA_MULT,
    uv.u, uv.v, uv.w, uv.h,
    0, 0, alpha,
    ship.body.vel.x, ship.body.vel.y, ship.body.angVel,
  );
}

/** Dormant asteroids are always beyond the active zone, so they only ever draw
 *  as dots (there's no module data to draw in full). Culled when off-screen. */
export function pushProxyDots(r: Renderer, view: ViewLod, proxies: AsteroidProxy[]): void {
  const uv = r.atlas.lookup.get("lod_dot_rock");
  if (!uv) return;
  for (const a of proxies) {
    if (offScreen(view, a.pos.x, a.pos.y, a.extent)) continue;
    const size = dotSize(view, a.extent);
    r.pushTileRaw(
      a.pos.x, a.pos.y,
      size, size,
      0, FLAG_ALPHA_MULT,
      uv.u, uv.v, uv.w, uv.h,
      0, 0, 1,
      a.vel.x, a.vel.y, a.angVel,
    );
  }
}

export function pushItemDot(r: Renderer, it: SpaceItem, view: ViewLod, alpha: number): void {
  const uv = r.atlas.lookup.get("lod_dot_item");
  if (!uv) return;
  const size = dotSize(view, getItemDef(it.defId).sizeM * 0.5);
  r.pushTileRaw(
    it.pos.x, it.pos.y,
    size, size,
    0, FLAG_ALPHA_MULT,
    uv.u, uv.v, uv.w, uv.h,
    0, 0, alpha,
    it.vel.x, it.vel.y, it.angVel,
  );
}
